import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelSelect as _vModelSelect, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, vShow as _vShow } from "vue"

const _hoisted_1 = { class: "bg-white rounded-lg border border-gray-200 shadow-sm py-3 px-3 mb-4" }
const _hoisted_2 = { class: "mb-2 flex items-center justify-between gap-2" }
const _hoisted_3 = { class: "grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-9" }
const _hoisted_4 = { class: "w-full" }
const _hoisted_5 = { class: "w-full" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { value: "" }
const _hoisted_8 = ["value"]
const _hoisted_9 = { class: "w-full md:flex-1" }
const _hoisted_10 = ["value"]
const _hoisted_11 = { class: "w-full md:flex-1" }
const _hoisted_12 = { class: "w-full md:flex-1" }
const _hoisted_13 = { class: "w-full md:flex-1" }
const _hoisted_14 = { class: "w-full md:flex-1" }
const _hoisted_15 = { class: "w-full md:flex-1" }
const _hoisted_16 = { class: "w-full md:flex-1" }
const _hoisted_17 = { class: "flex items-center gap-3 pt-1" }
const _hoisted_18 = { class: "flex gap-2" }
const _hoisted_19 = { class: "flex justify-end pt-1 pb-2" }

import { reactive, ref, computed, watch, onMounted, onUnmounted } from 'vue'
import type { Filters, ProduitCatalogue, CategorieNode } from '@/types'
import { getVendeurMarques } from '@/api/brands'
import type { Brand } from '@/types/brand'
import { getCollections } from '@/api/collections'
import type { CollectionOption } from '@/api/collections'
import { useAuthStore } from '@/stores/auth'

interface Props {
  products: ProduitCatalogue[]
  categoryOptions?: { id_categorie: number; label: string }[]
  categoriesTree?: CategorieNode[]
  // New: allow parent to pass initial filters (for server-side filtering + URL sync)
  initialFilters?: Partial<Filters>
}

interface Emits {
  (e: 'update:filtered-products', products: ProduitCatalogue[]): void
  // New: emit current filter criteria so parent can perform server-side filtering
  (e: 'filter', filters: Filters): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductFilters',
  props: {
    products: {},
    categoryOptions: {},
    categoriesTree: {},
    initialFilters: {}
  },
  emits: ["update:filtered-products", "filter"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

const isFiltering = ref(false)
const isCollapsed = ref(true)
const brands = ref<Brand[]>([])
const collections = ref<CollectionOption[]>([])
const authStore = useAuthStore()
const filters = reactive<Filters>({
  reference: '',
  marque: '',
  collection: '',
  categories: '',
  catLevel1Id: '',
  catLevel2Id: '',
  catLevel3Id: '',
  prixMin: '',
  prixMax: '',
  statut: '',
  dateFrom: '',
  dateTo: '',
  tri: 'date',
  remise: ''
})

const level1Options = computed(() => props.categoriesTree || [])
const level2Options = computed(() => {
  if (!filters.catLevel1Id || !props.categoriesTree) return []
  const parent = props.categoriesTree.find(c => String(c.id_categorie) === String(filters.catLevel1Id))
  return parent?.children || []
})
const level3Options = computed(() => {
  if (!filters.catLevel2Id || !level2Options.value.length) return []
  const parent = level2Options.value.find(c => String(c.id_categorie) === String(filters.catLevel2Id))
  return parent?.children || []
})

watch(() => filters.catLevel1Id, (newVal) => {
  filters.catLevel2Id = ''
  filters.catLevel3Id = ''
  filters.categories = String(newVal || '')
})

watch(() => filters.catLevel2Id, (newVal) => {
  filters.catLevel3Id = ''
  filters.categories = String(newVal || filters.catLevel1Id || '')
})

watch(() => filters.catLevel3Id, (newVal) => {
  filters.categories = String(newVal || filters.catLevel2Id || filters.catLevel1Id || '')
})

const filteredProducts = computed(() => {
  let filtered = props.products

  if (filters.reference) {
    filtered = filtered.filter(p =>
        p.reference.toLowerCase().includes(filters.reference.toLowerCase())
    )
  }

  if (filters.marque) {
    filtered = filtered.filter(p =>
        p.marque === filters.marque
    )
  }

  if (filters.collection) {
    filtered = filtered.filter(p =>
        p.collection === filters.collection
    )
  }

  if (filters.categories) {
    const selectedId = Number(filters.categories)
    filtered = filtered.filter(
        p =>
            p.catLevel1Id === selectedId ||
            p.catLevel2Id === selectedId ||
            p.catLevel3Id === selectedId
    )
  }

  if (filters.prixMin) {
    filtered = filtered.filter(p => p.prix >= parseFloat(filters.prixMin))
  }

  if (filters.prixMax) {
    filtered = filtered.filter(p => p.prix <= parseFloat(filters.prixMax))
  }

  if (filters.statut) {
    filtered = filtered.filter(p => p.statut === filters.statut)
  }

  if (filters.remise === 'remise') {
    filtered = filtered.filter(p =>
        p.prixReduit != null && p.prixReduit > 0 && p.prixReduit < p.prix)
  } else if (filters.remise === 'plein-tarif') {
    filtered = filtered.filter(p =>
        !(p.prixReduit != null && p.prixReduit > 0 && p.prixReduit < p.prix))
  }

  if (filters.dateFrom || filters.dateTo) {
    const from = filters.dateFrom ? new Date(filters.dateFrom) : null
    const to = filters.dateTo ? new Date(filters.dateTo) : null

    filtered = filtered.filter(p => {
      if (!p.date) return false
      const d = new Date(p.date as any)
      if (Number.isNaN(d.getTime())) return false

      if (from && d < from) return false

      if (to) {
        const endOfDay = new Date(to)
        endOfDay.setHours(23, 59, 59, 999)
        if (d > endOfDay) return false
      }
      return true
    })
  }

  return filtered
})

// Debounce server-side filtering to avoid excessive requests and improve UX
let filterDebounceTimer: number | undefined
watch(
  filters,
  () => {
    // Immediate update for local filtering
    emit('update:filtered-products', filteredProducts.value)
    // Debounced server notification
    if (filterDebounceTimer) window.clearTimeout(filterDebounceTimer)
    filterDebounceTimer = window.setTimeout(() => {
      emit('filter', { ...filters })
    }, 400)
  },
  { deep: true }
)

const handleFilter = async () => {
  isFiltering.value = true
  await new Promise(resolve => setTimeout(resolve, 500))
  emit('update:filtered-products', filteredProducts.value)
  emit('filter', { ...filters })
  isFiltering.value = false
}

// Computed property to check if any filter is active
const hasActiveFilters = computed(() => {
  return Object.entries(filters).some(([key, value]) => {
    if (key === 'tri') return value !== 'date'
    return value !== null && value !== undefined && value !== ''
  })
})

const clearFilters = () => {
  filters.reference = ''
  filters.marque = ''
  filters.collection = ''
  filters.categories = ''
  filters.catLevel1Id = ''
  filters.catLevel2Id = ''
  filters.catLevel3Id = ''
  filters.prixMin = ''
  filters.prixMax = ''
  filters.statut = ''
  filters.dateFrom = ''
  filters.dateTo = ''
  filters.tri = 'date'
  filters.remise = ''

  emit('update:filtered-products', props.products)
  emit('filter', { ...filters })
}

// Charger les marques et collections
const loadBrands = async () => {
  try {
    // Vérifier que l'authentification est initialisée
    if (!authStore.isInitialized) {
      await authStore.checkAuth()
    }
    
    // Vérifier que l'utilisateur est authentifié
    if (!authStore.isAuthenticated || !authStore.user?.id_vendeur) {
      brands.value = []
      return
    }
    
    brands.value = await getVendeurMarques()
  } catch (error) {
    brands.value = []
  }
}

const loadCollections = async () => {
  try {
    collections.value = await getCollections('fr')
  } catch (error) {
    collections.value = []
  }
}

// Gérer le collapse en fonction de la largeur de l'écran
let resizeHandler: (() => void) | null = null

onMounted(async () => {
  // Charger les marques et collections
  await Promise.all([loadBrands(), loadCollections()])
    // Initialize filters from props.initialFilters if provided
    if (props.initialFilters) {
      const init = props.initialFilters
      if (init.reference != null) filters.reference = String(init.reference)
      if (init.marque != null) filters.marque = String(init.marque)
      if (init.collection != null) filters.collection = String(init.collection)
      if (init.categories != null) filters.categories = String(init.categories)
      if (init.catLevel1Id != null) filters.catLevel1Id = String(init.catLevel1Id)
      if (init.catLevel2Id != null) filters.catLevel2Id = String(init.catLevel2Id)
      if (init.catLevel3Id != null) filters.catLevel3Id = String(init.catLevel3Id)
      if (init.prixMin != null) filters.prixMin = String(init.prixMin)
      if (init.prixMax != null) filters.prixMax = String(init.prixMax)
      if (init.statut != null) filters.statut = String(init.statut)
      if (init.dateFrom != null) filters.dateFrom = String(init.dateFrom)
      if (init.dateTo != null) filters.dateTo = String(init.dateTo)
      if (init.tri != null) filters.tri = String(init.tri)
      if (init.remise != null) filters.remise = String(init.remise)
      // Emit once to inform parent of initial state
      emit('filter', { ...filters })
    }
  
  let lastWidth = window.innerWidth
  isCollapsed.value = lastWidth < 768

  resizeHandler = () => {
    const newWidth = window.innerWidth
    if (newWidth !== lastWidth) {
      lastWidth = newWidth
      isCollapsed.value = newWidth < 768
    }
  }
  window.addEventListener('resize', resizeHandler)
})

onUnmounted(() => {
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
  }
  if (filterDebounceTimer) window.clearTimeout(filterDebounceTimer)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[13] || (_cache[13] = _createElementVNode("h3", { class: "text-sm font-semibold text-black" }, "Filtrer la liste", -1)),
      _createElementVNode("button", {
        type: "button",
        class: "flex items-center gap-1 text-xs font-medium text-black px-2 py-1 border border-gray-300 rounded md:hidden",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (isCollapsed.value = !isCollapsed.value))
      }, [
        _createElementVNode("i", {
          class: _normalizeClass(isCollapsed.value ? 'fas fa-chevron-down' : 'fas fa-chevron-up')
        }, null, 2),
        _createTextVNode(" " + _toDisplayString(isCollapsed.value ? 'Afficher' : 'Masquer'), 1)
      ])
    ]),
    _withDirectives(_createElementVNode("div", null, [
      _createElementVNode("form", {
        onSubmit: _cache[12] || (_cache[12] = _withModifiers(() => {}, ["prevent"])),
        class: "space-y-2 pt-2"
      }, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _cache[14] || (_cache[14] = _createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-0.5" }, " Référence ", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.reference) = $event)),
              type: "text",
              placeholder: "Rechercher par référence",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, null, 512), [
              [
                _vModelText,
                filters.reference,
                void 0,
                { trim: true }
              ]
            ])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _cache[15] || (_cache[15] = _createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-0.5" }, " Marque ", -1)),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.marque) = $event)),
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500",
              disabled: brands.value.length === 0
            }, [
              _createElementVNode("option", _hoisted_7, _toDisplayString(brands.value.length === 0 ? 'Chargement...' : 'Toutes les marques'), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(brands.value, (marque) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: marque.number || marque.id,
                  value: marque.name
                }, _toDisplayString(marque.name), 9, _hoisted_8))
              }), 128))
            ], 8, _hoisted_6), [
              [_vModelSelect, filters.marque]
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _cache[17] || (_cache[17] = _createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-0.5" }, " Collection ", -1)),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((filters.collection) = $event)),
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, [
              _cache[16] || (_cache[16] = _createElementVNode("option", { value: "" }, "Toutes les collections", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(collections.value, (collection) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: collection.id_collection,
                  value: collection.libelle
                }, _toDisplayString(collection.libelle), 9, _hoisted_10))
              }), 128))
            ], 512), [
              [_vModelSelect, filters.collection]
            ])
          ]),
          _createElementVNode("div", _hoisted_11, [
            _cache[19] || (_cache[19] = _createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-0.5" }, " Statut ", -1)),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filters.statut) = $event)),
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, _cache[18] || (_cache[18] = [
              _createElementVNode("option", { value: "" }, "Tous les statuts", -1),
              _createElementVNode("option", { value: "en-vente" }, "En vente", -1),
              _createElementVNode("option", { value: "hors-ligne" }, "Hors ligne", -1),
              _createElementVNode("option", { value: "rupture" }, "Rupture", -1)
            ]), 512), [
              [_vModelSelect, filters.statut]
            ])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _cache[21] || (_cache[21] = _createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-0.5" }, " Remise ", -1)),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filters.remise) = $event)),
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, _cache[20] || (_cache[20] = [
              _createElementVNode("option", { value: "" }, "Tous les produits", -1),
              _createElementVNode("option", { value: "remise" }, "Produits remisés", -1),
              _createElementVNode("option", { value: "plein-tarif" }, "Hors remise", -1)
            ]), 512), [
              [_vModelSelect, filters.remise]
            ])
          ]),
          _createElementVNode("div", _hoisted_13, [
            _cache[22] || (_cache[22] = _createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-0.5" }, " Prix min ", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((filters.prixMin) = $event)),
              type: "number",
              placeholder: "0",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, null, 512), [
              [
                _vModelText,
                filters.prixMin,
                void 0,
                { number: true }
              ]
            ])
          ]),
          _createElementVNode("div", _hoisted_14, [
            _cache[23] || (_cache[23] = _createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-0.5" }, " Prix max ", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((filters.prixMax) = $event)),
              type: "number",
              placeholder: "1000",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, null, 512), [
              [
                _vModelText,
                filters.prixMax,
                void 0,
                { number: true }
              ]
            ])
          ]),
          _createElementVNode("div", _hoisted_15, [
            _cache[24] || (_cache[24] = _createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-0.5" }, " Date début ", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((filters.dateFrom) = $event)),
              type: "date",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, null, 512), [
              [_vModelText, filters.dateFrom]
            ])
          ]),
          _createElementVNode("div", _hoisted_16, [
            _cache[25] || (_cache[25] = _createElementVNode("label", { class: "block text-xs font-medium text-gray-700 mb-0.5" }, " Date fin ", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((filters.dateTo) = $event)),
              type: "date",
              class: "w-full px-3.5 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            }, null, 512), [
              [_vModelText, filters.dateTo]
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_17, [
          _cache[28] || (_cache[28] = _createElementVNode("label", { class: "text-xs font-medium text-gray-700" }, "Trier par", -1)),
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass([
              'px-3 py-1 text-xs font-medium rounded border transition-colors',
              filters.tri === 'date'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]),
              onClick: _cache[10] || (_cache[10] = ($event: any) => (filters.tri = 'date'))
            }, _cache[26] || (_cache[26] = [
              _createElementVNode("i", { class: "fas fa-calendar-alt mr-1" }, null, -1),
              _createTextVNode("Date ")
            ]), 2),
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass([
              'px-3 py-1 text-xs font-medium rounded border transition-colors',
              filters.tri === 'alphabet'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]),
              onClick: _cache[11] || (_cache[11] = ($event: any) => (filters.tri = 'alphabet'))
            }, _cache[27] || (_cache[27] = [
              _createElementVNode("i", { class: "fas fa-sort-alpha-down mr-1" }, null, -1),
              _createTextVNode("Alphabet ")
            ]), 2)
          ])
        ]),
        _createElementVNode("div", _hoisted_19, [
          (hasActiveFilters.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                onClick: clearFilters,
                class: "inline-flex items-center px-3.5 py-1 text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-red-500"
              }, _cache[29] || (_cache[29] = [
                _createElementVNode("i", { class: "fas fa-undo mr-1 text-xs" }, null, -1),
                _createTextVNode(" Réinitialiser ")
              ])))
            : _createCommentVNode("", true)
        ])
      ], 32)
    ], 512), [
      [_vShow, !isCollapsed.value]
    ])
  ]))
}
}

})